import React, { useMemo } from 'react';

import { useFormikContext } from 'formik';

import { formatPhoneNumber } from 'lib/helpers/utility';
import useCopy from 'lib/hooks/useCopy';

import SectionList from 'components/practice/UserPreferences/components/SectionList';
import { withErrorBoundary } from 'components/shared/Error/Boundary';
import Icon from 'components/shared/Icon/Icon';
import ImageUploader from 'components/shared/ImageUploader/ImageUploader';
import Input from 'components/shared/Input/Input';

const PracticeIdentity = ({ practice, setPractice }) => {
  const { setFieldValue, values, errors } = useFormikContext();
  const { copyToClipboard } = useCopy();

  const imageHandlers = useMemo(() => {
    if (!practice?.header_photo) return [];
    try {
      const parsed = JSON.parse(practice.header_photo);
      const firstKey = Object.keys(parsed)[0];
      return firstKey ? [parsed[firstKey]] : [];
    } catch {
      return [];
    }
  }, [practice?.header_photo]);

  const handleDBANameChange = (e) => {
    setFieldValue('dbaName', e?.target?.value);
  };

  return (
    <SectionList
      title="Practice identity"
      description="Set your logo, name, and contact details to personalize how your practice is displayed and reached.">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-500 text-neutral-900">Upload image</span>
            <span className="text-xs text-neutral-500">Min 400x400px PNG or JPEG</span>
          </div>
          <ImageUploader
            className="mt-1"
            handlers={imageHandlers}
            onDelete={() => {
              setPractice((prev) => ({ ...prev, header_photo: null }));
            }}
            onUpload={(file) => {
              const ext = file.name.split('.').pop();
              const minimal = { [ext]: file.handle };

              const header_photo = JSON.stringify(minimal);
              setPractice((prev) => ({ ...prev, header_photo }));
            }}
          />
        </div>

        {/* Practice Names */}
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Practice legal name"
            placeholder="Legal Name"
            type="text"
            name="legal_name"
            value={practice?.name}
            disabled
          />
          <Input
            label="DBA name"
            type="text"
            placeholder="DBA Name"
            name="dba_name"
            value={values?.dbaName}
            onChange={handleDBANameChange}
            error={errors?.dbaName}
            tooltip="The 'Doing Business As' (DBA) name entered here will be used in all patient communications, including appointment reminders and billing notices. If left blank, communications will default to the legal name of the practice. Use a DBA name that patients easily recognize to enhance clarity and branding in your interactions."
          />
        </div>

        {/* Contact Information */}
        <div className="grid grid-cols-2 gap-4">
          <div className="relative">
            <Input
              label="Twilio phone"
              type="text"
              disabled
              placeholder="Twilio Number"
              name="twilio_phone"
              value={
                formatPhoneNumber(practice?.twilio_phone) ||
                'Practice does not have a Twilio phone at this time.'
              }
            />
            {practice?.twilio_phone && (
              <Icon
                icon="copy-mono"
                size={20}
                className="absolute right-3 top-[38px] cursor-pointer text-neutral-500 hover:text-primary-500"
                onClick={() => copyToClipboard(practice?.twilio_phone)}
              />
            )}
          </div>
          <div className="relative">
            <Input
              label="E-fax number"
              type="text"
              disabled
              placeholder="eFax Number"
              name="fax_number"
              value={
                formatPhoneNumber('+1' + practice?.eFax?.fax_number) ||
                'Practice does not have a fax number at this time.'
              }
            />
            {practice?.eFax?.fax_number && (
              <Icon
                icon="copy-mono"
                size={20}
                className="absolute right-3 top-[38px] cursor-pointer text-neutral-500 hover:text-primary-500"
                onClick={() => copyToClipboard('+1' + practice?.eFax?.fax_number)}
              />
            )}
          </div>
        </div>
      </div>
    </SectionList>
  );
};

export default withErrorBoundary(PracticeIdentity);
